import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Scale, Menu, X } from 'lucide-react';
import styles from './LandingHeader.module.css';

// Homepage section anchors, plus two real routes.
//
// "Questions" points at the standalone /faq page rather than the homepage FAQ
// section: it is a real indexed route and resolves from every public page.
//
// "Law Firms" replaces the "How we make money" anchor. REDESIGN.md §4.1 keeps
// the header plaintiff-only and routes attorneys through the footer Partners
// column and the door cards; this is a deliberate departure from that, giving
// the supply side one header entry. The "How we make money" section is still on
// the homepage — it just no longer has a nav link pointing at it.
const NAV_LINKS = [
  { label: 'How it works', anchor: 'how' },
  { label: 'What it costs', anchor: 'cost' },
  { label: 'Law Firms', path: '/for-law-firms' },
  { label: 'Questions', path: '/faq' },
];

const LandingHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (location.pathname !== '/') {
      navigate('/#' + id);
    }
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  // A link is either a homepage anchor or a route; both close the drawer.
  const activate = (item) => (event) => {
    event.preventDefault();
    if (item.path) go(item.path);
    else scrollTo(item.anchor);
  };

  const hrefFor = (item) => item.path || `/#${item.anchor}`;

  const logo = (size, textClass) => (
    <>
      <Scale size={size} aria-hidden="true" />
      <span className={textClass}>MyCaseLoan</span>
    </>
  );

  return (
    <>
      <header className={styles.header} data-testid="landing-header">
        <div className={styles.inner}>
          <button
            type="button"
            className={`${styles.logo} ${styles.logoButton}`}
            onClick={() => navigate('/')}
            aria-label="MyCaseLoan home"
            data-testid="logo"
          >
            {logo(28, styles.logoText)}
          </button>

          <nav className={styles.links} aria-label="Primary">
            {NAV_LINKS.map((item) => (
              <a
                key={item.label}
                href={hrefFor(item)}
                className={styles.link}
                onClick={activate(item)}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOnDark}`}
              onClick={() => navigate('/login')}
              data-testid="login-button-header"
            >
              Log in
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnAccent}`}
              onClick={() => navigate('/apply')}
              data-testid="apply-now-button-header"
            >
              Apply
            </button>
          </div>

          <button
            type="button"
            className={styles.toggle}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            aria-controls="mcl-mobile-nav"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={26} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div
        className={`${styles.overlay} ${mobileOpen ? styles.overlayOpen : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* `inert` while closed so keyboard focus cannot walk into an off-screen
          drawer. React 19 treats this as a real boolean prop — `inert=""` would
          be read as false and dropped, so pass `true`. */}
      <div
        id="mcl-mobile-nav"
        className={`${styles.drawer} ${mobileOpen ? styles.drawerOpen : ''}`}
        data-testid="mobile-nav"
        inert={mobileOpen ? undefined : true}
      >
        <div className={styles.drawerHead}>
          <span className={styles.logo}>{logo(22, styles.logoText)}</span>
          <button
            type="button"
            className={styles.drawerClose}
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Primary, mobile">
          {NAV_LINKS.map((item) => (
            <a
              key={item.label}
              href={hrefFor(item)}
              className={styles.drawerLink}
              onClick={activate(item)}
            >
              {item.label}
            </a>
          ))}
          <hr className={styles.drawerRule} />
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOnDark} ${styles.drawerBtn}`}
            onClick={() => go('/login')}
          >
            Log in
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnAccent} ${styles.drawerBtn}`}
            onClick={() => go('/apply')}
          >
            Apply
          </button>
        </nav>
      </div>
    </>
  );
};

export default LandingHeader;
